import { useState } from 'react';
import { Layers, MessageSquare, ListChecks, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import type { TabFrameworksTalkTracks } from '../../../../types/craft';

interface Props {
  data: TabFrameworksTalkTracks;
}

export default function FrameworksTalkTracksTab({ data }: Props) {
  const hasData = data?.frameworks?.length || data?.talk_tracks?.length || data?.execution_templates?.length;
  if (!hasData) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.frameworks?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Frameworks</h4>
          </div>
          <div className="space-y-3">
            {data.frameworks.map((fw, i) => (
              <FrameworkCard key={i} framework={fw} />
            ))}
          </div>
        </div>
      )}

      {data.talk_tracks?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-ink">Talk Tracks</h4>
          </div>
          <div className="space-y-3">
            {data.talk_tracks.map((tt, i) => (
              <TalkTrackCard key={i} track={tt} />
            ))}
          </div>
        </div>
      )}

      {data.execution_templates?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="w-4 h-4 text-app-warning" />
            <h4 className="text-sm font-semibold text-app-ink">Execution Templates</h4>
          </div>
          <div className="space-y-3">
            {data.execution_templates.map((tmpl, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg p-4">
                <h5 className="text-sm font-semibold text-app-ink mb-3">{tmpl.title}</h5>
                {tmpl.steps?.length > 0 && (
                  <ol className="space-y-2 mb-3">
                    {tmpl.steps.map((step, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-app-focus/10 text-app-focus flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                          {j + 1}
                        </span>
                        <p className="text-xs text-app-ink">{step}</p>
                      </li>
                    ))}
                  </ol>
                )}
                {tmpl.tips?.length > 0 && (
                  <div className="bg-app-positive/10 border border-app-positive rounded-lg px-3 py-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-app-positive">Tips</span>
                    <ul className="mt-1 space-y-1">
                      {tmpl.tips.map((tip, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-app-positive">
                          <span className="w-1 h-1 rounded-full bg-app-positive mt-1.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FrameworkCard({ framework: fw }: { framework: { name: string; relevance: string; when_to_use: string; when_not_to_use: string } }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-app-surface border border-app-line rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-app-canvas transition-colors"
      >
        <h5 className="text-sm font-semibold text-app-ink">{fw.name}</h5>
        {expanded ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-app-line pt-3">
          {fw.relevance && (
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-app-muted">Relevance</span>
              <p className="text-xs text-app-ink mt-0.5">{fw.relevance}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fw.when_to_use && (
              <div className="bg-app-positive/10 border border-app-positive rounded-lg px-3 py-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-app-positive">When to Use</span>
                <p className="text-xs text-app-positive mt-0.5">{fw.when_to_use}</p>
              </div>
            )}
            {fw.when_not_to_use && (
              <div className="bg-app-critical/10 border border-app-critical rounded-lg px-3 py-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-app-critical">When Not to Use</span>
                <p className="text-xs text-app-critical mt-0.5">{fw.when_not_to_use}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TalkTrackCard({ track }: { track: { label: string; context: string; track: string } }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(track.track);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-app-surface border border-app-line rounded-lg p-4 group relative">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h5 className="text-sm font-semibold text-app-ink">{track.label}</h5>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-app-canvas transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-app-positive" /> : <Copy className="w-3.5 h-3.5 text-app-muted" />}
        </button>
      </div>
      {track.context && (
        <p className="text-xs text-app-muted mb-2"><span className="font-medium">Context:</span> {track.context}</p>
      )}
      <div className="bg-app-focus/10 border border-app-focus/30 rounded-lg px-3 py-2">
        <p className="text-xs text-app-focus leading-relaxed italic">"{track.track}"</p>
      </div>
    </div>
  );
}
